import { useState, useEffect } from 'react';
import { type Producer } from '../../../shared/types';
import { useToast } from '../../../shared/hooks/useToast';
import {
  getCurrentSessionToken,
  fetchAllUsers,
  fetchUserById,
  createUser,
  toggleUserStatus,
  deleteUser,
  updateUserById,
} from '../../auth';
import {
  fetchAccount,
  updateOverdraft,
  createAccount,
  registerFinancialMovement,
} from '../services/accountService';
import {
  fetchGrainStocks,
  updateGrainStocks,
  initGrainStocks,
} from '../services/grainService';

interface ConfirmModalState {
  isOpen: boolean;
  title: string;
  description: string;
  onConfirm: () => void;
  type: 'success' | 'danger' | 'warning';
  confirmText?: string;
  isLoading?: boolean;
  loadingText?: string;
}

const CONFIRM_MODAL_DEFAULT: ConfirmModalState = {
  isOpen: false,
  title: '',
  description: '',
  onConfirm: () => { },
  type: 'success',
  confirmText: 'Confirmar',
  isLoading: false,
  loadingText: 'Confirmando...',
};

export function useProducers() {
  const [producers, setProducers] = useState<Producer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProducer, setEditingProducer] = useState<Producer | null>(null);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>(CONFIRM_MODAL_DEFAULT);

  const { toasts, showToast, dismissToast } = useToast();

  // ── Load ────────────────────────────────────────────────────────────
  useEffect(() => {
    const loadProducers = async () => {
      try {
        setIsLoading(true);
        const token = await getCurrentSessionToken();
        if (!token) throw new Error('No active session token found');

        const usersData = await fetchAllUsers(token, currentPage, 10, searchTerm);

        let usersList: any[] = [];
        if (usersData) {
          if (Array.isArray(usersData)) {
            usersList = usersData;
            setTotalPages(1);
          } else if (Array.isArray(usersData.users)) {
            usersList = usersData.users;
            if (usersData.pagination) setTotalPages(usersData.pagination.pages || 1);
          }
        }

        usersList.sort((a: any, b: any) => (a.full_name || '').localeCompare(b.full_name || ''));
        usersList = usersList.filter((u: any) => {
          const roles: string[] = (u.roles || []).map((r: string) => r.toUpperCase());
          return roles.includes('PRODUCER') && !roles.includes('ADMIN');
        });

        const mapped = await Promise.all(
          usersList.map(async (user: any) => {
            let balance = 0, overdraft = 0, soja = 0, maiz = 0, trigo = 0, sorgo = 0, girasol = 0, mani = 0;

            try {
              const accountData = await fetchAccount(user.id, token);
              if (accountData) {
                balance = accountData.balance ?? 0;
                overdraft = accountData.overdraft_limit ?? 0;
              }
            } catch {
              console.warn(`No financial account found for user ${user.id}`);
            }

            try {
              const grainData = await fetchGrainStocks(user.id, token);
              if (Array.isArray(grainData)) {
                grainData.forEach((grain: any) => {
                  const cereal = (grain.cereal || '').toLowerCase();
                  if (cereal === 'soja') soja = grain.tonnage;
                  else if (cereal === 'maiz') maiz = grain.tonnage;
                  else if (cereal === 'trigo') trigo = grain.tonnage;
                  else if (cereal === 'sorgo') sorgo = grain.tonnage;
                  else if (cereal === 'girasol') girasol = grain.tonnage;
                  else if (cereal === 'mani') mani = grain.tonnage;
                });
              }
            } catch {
              console.warn(`No grain stocks found for user ${user.id}`);
            }

            return {
              id: user.id,
              name: user.full_name,
              cuit: user.CUIT,
              email: user.email,
              phone: user.phone || '',
              status: ((user.status || '').toLowerCase() === 'active' ? 'active' : 'inactive') as 'active' | 'inactive',
              balance, overdraft, soja, maiz, trigo, sorgo, girasol, mani,
            };
          })
        );

        setProducers(mapped);
      } catch (e) {
        console.error('Error loading producers:', e);
        setProducers([]);
        setTotalPages(1);
      } finally {
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    };

    loadProducers();
  }, [currentPage, searchTerm]);

  // ── Handlers ────────────────────────────────────────────────────────
  const handleToggleStatus = (id: number | string) => {
    const producer = producers.find(p => p.id === id);
    const action = producer?.status === 'active' ? 'inactivar' : 'activar';
    const nextStatus = producer?.status === 'active' ? 'INACTIVE' : 'ACTIVE';

    setConfirmModal({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Productor`,
      description: `¿Estás seguro que deseas ${action} al productor ${producer?.name}?`,
      type: action === 'inactivar' ? 'warning' : 'success',
      confirmText: action === 'inactivar' ? 'Inactivar' : 'Activar',
      loadingText: action === 'inactivar' ? 'Inactivando...' : 'Activando...',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          const token = await getCurrentSessionToken();
          if (token && typeof id === 'string') await toggleUserStatus(id, nextStatus, token);
          setProducers(prev => prev.map(p => p.id === id ? { ...p, status: p.status === 'active' ? 'inactive' : 'active' } : p));
          showToast(
            `Productor ${action === 'activar' ? 'activado' : 'inactivado'}`,
            action === 'activar' ? 'success' : 'warning',
            `${producer?.name} ahora está ${action === 'activar' ? 'activo' : 'inactivo'}.`
          );
          setConfirmModal(prev => ({ ...prev, isLoading: false, isOpen: false }));
        } catch (e) {
          showToast('Error de actualización', 'danger', `No se pudo cambiar el estado de ${producer?.name}.`);
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
        setIsModalOpen(false);
      },
    });
  };

  const handleDelete = (id: number | string) => {
    const producer = producers.find(p => p.id === id);
    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Productor',
      description: `¿Estás seguro que deseas eliminar a ${producer?.name}? Esta acción no se puede deshacer.`,
      type: 'danger',
      confirmText: 'Eliminar',
      loadingText: 'Eliminando...',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          const token = await getCurrentSessionToken();
          if (token && typeof id === 'string') await deleteUser(id, token);
          setProducers(prev => prev.filter(p => p.id !== id));
          showToast('Productor eliminado', 'danger', `${producer?.name} fue eliminado del sistema.`);
          setConfirmModal(prev => ({ ...prev, isLoading: false, isOpen: false }));
        } catch {
          showToast('Error de eliminación', 'danger', `No se pudo eliminar a ${producer?.name}.`);
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
        setIsModalOpen(false);
      },
    });
  };

  const openCreateModal = () => {
    setEditingProducer(null);
    setIsModalOpen(true);
  };

  const openEditModal = async (producer: Producer) => {
    try {
      const token = await getCurrentSessionToken();
      if (token) {
        const fullUser = await fetchUserById(String(producer.id), token);
        setEditingProducer({ ...producer, phone: fullUser.phone || '' });
      } else {
        setEditingProducer(producer);
      }
    } catch {
      setEditingProducer(producer);
    }
    setIsModalOpen(true);
  };

  const handleSave = async (data: Partial<Producer>) => {
    try {
      const token = await getCurrentSessionToken();
      if (!token) throw new Error('No active session token found');

      if (editingProducer) {
        const userId = String(editingProducer.id);

        // 0. Update User Profile if name or phone changed
        if (data.name !== editingProducer.name || data.phone !== editingProducer.phone) {
          try {
            await updateUserById(userId, {
              full_name: data.name,
              phone: data.phone
            }, token);
          } catch (e) {
            console.error('Failed to update user profile:', e);
          }
        }

        // 1. Overdraft update (only if changed, create account if missing)
        if (data.overdraft !== editingProducer.overdraft) {
          try {
            await updateOverdraft(userId, data.overdraft || 0, token);
          } catch (accErr: any) {
            if (accErr?.message?.includes('404') || String(accErr).includes('404')) {
              try { await createAccount(userId, data.overdraft || 0, data.balance || 0, token); }
              catch (e) { console.error('Failed to create account as fallback:', e); }
            } else {
              console.error('Failed to update overdraft:', accErr);
            }
          }
        }

        // 2. Balance delta → financial movement
        const delta = (data.balance || 0) - (editingProducer.balance || 0);
        if (delta !== 0) {
          try {
            await registerFinancialMovement(userId, {
              amount: Math.abs(delta),
              type: delta > 0 ? 'CREDIT' : 'DEBIT',
              concept: 'Ajuste de saldo - Administrador',
            }, token);
          } catch (e) { console.error('Failed to register balance movement:', e); }
        }

        // 3. Grain stocks (only if changed)
        if (
          data.soja !== editingProducer.soja ||
          data.maiz !== editingProducer.maiz ||
          data.trigo !== editingProducer.trigo ||
          data.sorgo !== editingProducer.sorgo ||
          data.girasol !== editingProducer.girasol ||
          data.mani !== editingProducer.mani
        ) {
          try {
            await updateGrainStocks(userId, [
              { cereal: 'soja', tonnage: data.soja || 0 },
              { cereal: 'maiz', tonnage: data.maiz || 0 },
              { cereal: 'trigo', tonnage: data.trigo || 0 },
              { cereal: 'sorgo', tonnage: data.sorgo || 0 },
              { cereal: 'girasol', tonnage: data.girasol || 0 },
              { cereal: 'mani', tonnage: data.mani || 0 },
            ], 'Ajuste de stock administrador', token);
          } catch (e) { console.error('Failed to update grain stocks:', e); }
        }

        setProducers(prev => prev.map(p => p.id === editingProducer.id ? { ...p, ...data } as Producer : p));
        showToast('Productor actualizado', 'success', `Los datos de ${editingProducer.name} se guardaron correctamente.`);

      } else {
        const createdUser = await createUser({
          email: data.email || '',
          full_name: data.name || '',
          CUIT: data.cuit || '',
          role: 'PRODUCER',
          phone: data.phone || ''
        }, token);

        let accountOk = false;
        let grainOk = false;

        try {
          await createAccount(createdUser.id, data.overdraft || 0, data.balance || 0, token);
          accountOk = true;
        } catch (e: any) {
          showToast('Cuenta financiera no creada', 'warning', e?.message || 'Error desconocido.');
        }

        try {
          await initGrainStocks(createdUser.id, [
            { cereal: 'soja', tonnage: data.soja || 0 },
            { cereal: 'maiz', tonnage: data.maiz || 0 },
            { cereal: 'trigo', tonnage: data.trigo || 0 },
            { cereal: 'sorgo', tonnage: data.sorgo || 0 },
            { cereal: 'girasol', tonnage: data.girasol || 0 },
            { cereal: 'mani', tonnage: data.mani || 0 },
          ], token);
          grainOk = true;
        } catch (e: any) {
          showToast('Stock de granos no inicializado', 'warning', e?.message || 'Error desconocido.');
        }

        const newProducer: Producer = {
          id: createdUser.id,
          name: createdUser.full_name,
          cuit: createdUser.CUIT,
          email: createdUser.email,
          phone: data.phone || '',
          balance: data.balance || 0,
          overdraft: data.overdraft || 0,
          soja: data.soja || 0,
          maiz: data.maiz || 0,
          trigo: data.trigo || 0,
          sorgo: data.sorgo || 0,
          girasol: data.girasol || 0,
          mani: data.mani || 0,
          status: (createdUser.status || '').toLowerCase() === 'active' ? 'active' : 'inactive',
        };
        setProducers(prev => [newProducer, ...prev]);

        showToast(
          accountOk && grainOk ? 'Productor registrado' : 'Productor creado parcialmente',
          accountOk && grainOk ? 'success' : 'warning',
          accountOk && grainOk
            ? `${newProducer.name} fue creado y sus cuentas inicializadas correctamente.`
            : `${newProducer.name} fue creado, pero algún paso falló. Revisá los avisos anteriores.`
        );
      }
    } catch (e: any) {
      console.error('Error saving producer:', e);
      showToast('Error al guardar', 'danger', 'Ocurrió un error al guardar los datos del productor.');
      throw e;
    }
  };

  return {
    // State
    producers,
    isLoading,
    isInitialLoad,
    searchTerm,
    currentPage,
    totalPages,
    isModalOpen,
    editingProducer,
    confirmModal,
    toasts,
    // Actions
    setSearchTerm,
    setCurrentPage,
    setIsModalOpen,
    setConfirmModal,
    openCreateModal,
    openEditModal,
    handleSave,
    handleDelete,
    handleToggleStatus,
    dismissToast,
  };
}
